import { useEffect, useRef } from 'react'
import type { LatLng } from '@/types/structs'

/* Imperative polyline + circle overlays. @vis.gl/react-google-maps has no
 * Polyline/Circle components — we drive google.maps objects directly.
 *
 * Parity targets (custom_g_map.dart):
 *  - Polyline("route"): width 5, colour 0xFF1F4CF1 (opaque);
 *  - Circles: radius 4 METRES, fill 0xFF1F4CF1 (opaque -> fillOpacity 1,
 *    google's default 0.35 would be wrong), stroke 0xff0919c7 width 4. */

export function useRoutePolyline(
  map: google.maps.Map | null,
  points: LatLng[] | null,
  visible: boolean,
  color: string,
) {
  const ref = useRef<google.maps.Polyline | null>(null)

  useEffect(() => {
    if (!map) return
    if (!ref.current) {
      ref.current = new google.maps.Polyline({
        strokeColor: color,
        strokeOpacity: 1,
        strokeWeight: 5,
      })
    }
    ref.current.setPath(visible && points ? points : [])
    ref.current.setMap(map)
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [map, points, visible])

  useEffect(
    () => () => {
      ref.current?.setMap(null)
      ref.current = null
    },
    [],
  )
}

export function useDottedCircles(
  map: google.maps.Map | null,
  centers: LatLng[],
  visible: boolean,
  fillColor: string,
) {
  const pool = useRef<google.maps.Circle[]>([])

  useEffect(() => {
    if (!map) return
    const want = visible ? centers : []
    // grow pool
    while (pool.current.length < want.length) {
      pool.current.push(
        new google.maps.Circle({
          radius: 4,
          fillColor,
          fillOpacity: 1,
          strokeColor: '#0919C7',
          strokeOpacity: 1,
          strokeWeight: 4,
          clickable: false,
        }),
      )
    }
    pool.current.forEach((c, i) => {
      if (i < want.length) {
        c.setCenter(want[i]!)
        c.setMap(map)
      } else {
        c.setMap(null)
      }
    })
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [map, centers, visible])

  useEffect(
    () => () => {
      pool.current.forEach((c) => c.setMap(null))
      pool.current = []
    },
    [],
  )
}
